import type { SvelteComponent } from 'svelte';
import type { Constructor } from 'type-fest';
import * as z from 'zod';
import { svelteClientLegacy } from '$lib/svelte/SvelteClientLegacy';
import { asyncSingleton } from '$lib/utils/factory';
import { allValues } from '$lib/utils/promise';

function getBodyAttributeNotEmptyOrNull(attributeName: string): string | null {
    const body = document.querySelector('body');
    if (body == null) {
        return null;
    }

    const value = body.getAttribute(attributeName) ?? null;
    if (value == null || value === '') {
        return null;
    }
    return value;
}

function getUserId(): number | null {
    const id = getBodyAttributeNotEmptyOrNull('data-user-id');
    return id == null ? null : parseInt(id, 10);
}

function getCurrentCountry() {
    return getBodyAttributeNotEmptyOrNull('data-current-country');
}

const getContainer = asyncSingleton(async () => {
    const ShareableContainer = (await import('$lib/container/shareableContainer'))
        .ShareableContainer;

    return new ShareableContainer({
        svelteClient: svelteClientLegacy,
        browser: true,
        goafricaDomain: z.string().parse(import.meta.env.PUBLIC_GOAFRICA_DOMAIN ?? ''),
        mercureBaseUrl: z.string().parse(import.meta.env.PUBLIC_MERCURE_BASE_URL ?? ''),
        recaptchaSiteKey: z
            .string()
            .parse(import.meta.env.PUBLIC_RECAPTCHA_V3_PUBLIC_KEY ?? ''),
        initialCurrentUserId: getUserId(),
        initialCurrentUserData: null,
        currentCountry: getCurrentCountry(),
        gtmId: z.string().parse(import.meta.env.PUBLIC_GTM_ID ?? ''),
        facebookAppId: z.string().parse(import.meta.env.PUBLIC_FACEBOOK_APP_ID ?? ''),
        leaflet: {
            tileLayerAttribution: z
                .string()
                .parse(import.meta.env.PUBLIC_LEAFLET_TILE_LAYER_ATTRIBUTION ?? ''),
            tileLayerUrl: z
                .string()
                .parse(import.meta.env.PUBLIC_LEAFLET_TILE_LAYER_URL ?? ''),
        },
        searchHeader: {
            displayOnMobile:
                getBodyAttributeNotEmptyOrNull('data-search-header-display-on-mobile') ===
                'true',
            displayOnDesktop: true,
            searchHeaderStyle: 'simple',
        },
        initialLocale: getBodyAttributeNotEmptyOrNull('data-locale') ?? 'fr-FR',
        defaultUrlHelperAbsolute: false,
    });
});

const getWithContext = asyncSingleton(async () => {
    return (await import('$components/Context/WithContext.svelte')).default;
});

export async function svelteInitializeElements<
    Props extends Record<string, unknown>,
>(options: {
    elements: NodeListOf<HTMLElement> | HTMLElement[];
    getComponentClass: () => Promise<Constructor<SvelteComponent<Props>>>;
    getProps: (element: HTMLElement) => Props;
    getOptions?: (element: HTMLElement) => {
        target?: HTMLElement;
        cleanTargetBeforeRender?: boolean;
    };
}) {
    if (options.elements.length === 0) {
        return;
    }

    const { ComponentClass, container, WithContext } = await allValues({
        ComponentClass: options.getComponentClass(),
        container: getContainer(),
        WithContext: getWithContext(),
    });

    for (const element of options.elements) {
        let svelteElement;
        try {
            const o = options.getOptions?.(element) ?? {};
            const target = o.target ?? element;

            if (o.cleanTargetBeforeRender ?? true) {
                target.innerHTML = '';
            }

            svelteElement = new WithContext({
                target,

                props: {
                    container,
                    component: ComponentClass,
                    props: options.getProps(element),
                },
            });
        } catch (e) {
            svelteElement?.$destroy();
            console.error(e);
        }
    }
}
